import * as React from 'react';
import { compose, withHandlers, withState, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { TranslateProps } from 'util/i18n';
import { translate } from 'react-i18next';
import { skin, OemProps } from 'ui/styles/skin';
import { InstantMessageId, InstantMessageMap } from 'types';
import { instantMessageDismissed } from 'actions/shellActions';
import { State } from 'reducers';
import { getInstantMessages } from 'selectors/shellSelectors';
import { InstantMessage } from './InstantMessage';

import './InstantMessageContainer.css';
import { Fade } from 'ui/transitions/Fade';
import { X } from 'ui/shapes';


const RawInstantMessageContainer = ({
    messagesClone,
    messagesState,
    handleTransitionEnd,
    handleDismissMessage,
    handleClickCloseAll,
    styles,
    t,
}: Props) => {
    const messagesInOrder =
        Object.keys(messagesClone)
            .map(id => messagesClone[id])
            .sort((a, b) => a.utcTime - b.utcTime);

    return (
        <div id="instant-message-container">
            <Fade
                visible={ messagesInOrder.length > 0 }
                duration={ 400 }
                unmountOnExit={ true }
            >
                <header style={ styles.SectionHeader }>
                    <span>
                        { t('404') }
                    </span>
                    <span
                        id={ 'im-close-all' }
                        className="im-close"
                        onClick={ handleClickCloseAll }
                        tabIndex={ 0 }
                        style={ styles.Bordered }
                    >
                        <X width=".5em" height=".5em" style={ styles.FillForeground }/>
                    </span>
                </header>
            </Fade>
            <div className="imc-body">
                { messagesInOrder.map(m =>
                    <div
                        key={ m.id }
                        data-id={ m.id }
                        className={ `im-host imstate${messagesState[m.id]}` }
                        onTransitionEnd={ handleTransitionEnd }
                    >
                        <InstantMessage
                            { ...m }
                            onClickClose={ handleDismissMessage }
                        />
                    </div>
                ) }
            </div>
        </div>
    );
};

enum IMState {
    before,
    entering,
    visible,
    exiting,
}

type IMStateMap = { [id: string]: IMState };

type StateProps = {
    messages: InstantMessageMap;
};
type DispatchProps = {
    dismiss: (id: InstantMessageId | null) => void;
};
type RecomposeProps = {
    messagesClone: InstantMessageMap;
    messagesState: IMStateMap;
    handleDismissMessage: (id: InstantMessageId) => void;
    handleTransitionEnd: React.TransitionEventHandler<HTMLElement>;
    handleClickCloseAll: React.MouseEventHandler<HTMLElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setMessagesClone: (messages: InstantMessageMap) => void;
    _setMessagesState: (state: IMStateMap) => void;
};

const mapStateToProps = (state: State) => ({
    messages: getInstantMessages(state),
});
const mapDispToProps = {
    dismiss: instantMessageDismissed,
};

type Props = StateProps & DispatchProps & RecomposeProps & OemProps & TranslateProps;

export const InstantMessageContainer = compose<Props, {}>(
    skin,
    translate(),
    connect<StateProps, DispatchProps, {}>(mapStateToProps, mapDispToProps),
    withState('messagesClone', '_setMessagesClone', {}),
    withState('messagesState', '_setMessagesState', {}),
    withHandlers<Props & DispatchProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleClickCloseAll: ({ dismiss }) => (e: React.MouseEvent<HTMLElement>) => {
            dismiss(null);
        },
        handleDismissMessage: ({ dismiss }) => dismiss,
        handleTransitionEnd: ({ messagesState, _setMessagesState, messagesClone, _setMessagesClone }) => (e: React.TransitionEvent<HTMLElement>) => {
            // Clone the current state
            messagesState = { ...messagesState };
            messagesClone = { ...messagesClone };

            const { id } = e.currentTarget.dataset;
            switch (messagesState[id!]) {
                case IMState.entering:
                    // move to the entered list
                    messagesState[id!] = IMState.visible;
                    break;

                case IMState.exiting:
                    // Element finished animating out... remove it from internal state now
                    delete messagesState[id!];
                    delete messagesClone[id!];
                    break;

                default: // Ignore the other states
                    return;
            }
            _setMessagesState(messagesState);
            _setMessagesClone(messagesClone);
        },
    }),
    lifecycle<Props & _RecomposeProps, {}>({
        componentDidUpdate(oldProps: Props) {
            const { messages, messagesClone, _setMessagesClone, messagesState, _setMessagesState } = this.props;
            if (messages !== oldProps.messages) {
                // Determine which messages are being added and deleted
                const addedMessages = Object.keys(oldProps.messages).reduce((m, oldKey) => { delete m[oldKey]; return m; }, { ...messages });
                const removedMessages = Object.keys(messages).reduce((m, newKey) => { delete m[newKey]; return m; }, { ...oldProps.messages });
                const addedIds = Object.keys(addedMessages);
                const removedIds = Object.keys(removedMessages);

                // If at least one addition or one deletion then update our internal data
                if (addedIds.length > 0 || removedIds.length > 0) {
                    // Calculate new state (added messages becoming 'before' and deleted messages become 'exiting')
                    let newState = messagesState;
                    newState = addedIds.reduce((ms, id) => ({ ...ms, [id]: IMState.before }), newState);
                    newState = removedIds.reduce((ms, id) => ({ ...ms, [id]: IMState.exiting }), newState);
                    _setMessagesState(newState);

                    // Add the new messages now... (deleted messages will be removed after exit animation finishes)
                    _setMessagesClone({ ...messagesClone, ...addedMessages });

                    // Wait a brief delay and then switch from 'before' to 'entering' for all the new messages
                    setTimeout(() => {
                        newState = addedIds.reduce((ms, id) => ({ ...ms, [id]: IMState.entering }), newState);
                        _setMessagesState(newState);
                    }, 50);
                }
            }
        },
    }),
)(RawInstantMessageContainer);



// WEBPACK FOOTER //
// ./src/ui/main/InstantMessageContainer.tsx